import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Holding } from '../api/portfolio';
import { getCompanyColor } from '../utils/companyColors';
import { getCompanyName } from '../utils/companyNames';

interface TopPositionsChartProps {
  portfolio: Holding[];
}

// Known ETF top holdings (expanded for top 20)
const ETF_HOLDINGS: Record<string, Array<{ symbol: string; weight: number }>> = {
  'SPY': [
    { symbol: 'AAPL', weight: 7.1 },
    { symbol: 'MSFT', weight: 6.8 },
    { symbol: 'NVDA', weight: 6.2 },
    { symbol: 'AMZN', weight: 3.8 },
    { symbol: 'META', weight: 2.5 },
    { symbol: 'GOOGL', weight: 2.3 },
    { symbol: 'GOOG', weight: 1.9 },
    { symbol: 'BRK.B', weight: 1.7 },
    { symbol: 'TSLA', weight: 1.6 },
    { symbol: 'LLY', weight: 1.5 },
    { symbol: 'V', weight: 1.2 },
    { symbol: 'UNH', weight: 1.1 },
    { symbol: 'XOM', weight: 1.0 },
    { symbol: 'JPM', weight: 0.9 },
    { symbol: 'JNJ', weight: 0.9 },
    { symbol: 'WMT', weight: 0.8 },
    { symbol: 'PG', weight: 0.8 },
    { symbol: 'MA', weight: 0.7 },
    { symbol: 'HD', weight: 0.7 },
    { symbol: 'CVX', weight: 0.6 },
  ],
  'QQQ': [
    { symbol: 'AAPL', weight: 9.2 },
    { symbol: 'MSFT', weight: 8.5 },
    { symbol: 'NVDA', weight: 8.1 },
    { symbol: 'AMZN', weight: 5.4 },
    { symbol: 'META', weight: 4.8 },
    { symbol: 'GOOGL', weight: 3.1 },
    { symbol: 'GOOG', weight: 2.9 },
    { symbol: 'TSLA', weight: 2.7 },
    { symbol: 'AVGO', weight: 2.4 },
    { symbol: 'COST', weight: 2.1 },
  ],
  'VOO': [
    { symbol: 'AAPL', weight: 7.0 },
    { symbol: 'MSFT', weight: 6.7 },
    { symbol: 'NVDA', weight: 6.1 },
    { symbol: 'AMZN', weight: 3.7 },
    { symbol: 'META', weight: 2.4 },
    { symbol: 'GOOGL', weight: 2.2 },
    { symbol: 'GOOG', weight: 1.8 },
    { symbol: 'BRK.B', weight: 1.6 },
    { symbol: 'TSLA', weight: 1.5 },
    { symbol: 'LLY', weight: 1.4 },
  ],
  'VTI': [
    { symbol: 'AAPL', weight: 5.8 },
    { symbol: 'MSFT', weight: 5.5 },
    { symbol: 'NVDA', weight: 5.1 },
    { symbol: 'AMZN', weight: 3.1 },
    { symbol: 'META', weight: 2.0 },
    { symbol: 'GOOGL', weight: 1.8 },
    { symbol: 'GOOG', weight: 1.5 },
    { symbol: 'BRK.B', weight: 1.4 },
    { symbol: 'TSLA', weight: 1.3 },
    { symbol: 'LLY', weight: 1.2 },
  ],
};

type TopN = 5 | 10 | 20;

export default function TopPositionsChart({ portfolio }: TopPositionsChartProps) {
  const [topN, setTopN] = useState<TopN>(10);
  const [untangleETFs, setUntangleETFs] = useState(false);

  if (portfolio.length === 0) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Positions</h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          Add holdings to see your top positions
        </div>
      </div>
    );
  }

  const totalValue = portfolio.reduce((sum, h) => sum + (h.totalValue || 0), 0);

  // Calculate positions
  const getPositions = () => {
    if (!untangleETFs) {
      // Simple view: just show holdings as-is
      return portfolio
        .map(h => ({
          symbol: h.symbol,
          value: h.totalValue || 0,
          percentage: totalValue > 0 ? ((h.totalValue || 0) / totalValue) * 100 : 0,
          isETF: !!ETF_HOLDINGS[h.symbol],
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, topN);
    } else {
      // Untangled view: break down ETFs into underlying holdings
      const exposureMap = new Map<string, number>();

      portfolio.forEach(holding => {
        const holdingValue = holding.totalValue || 0;

        if (ETF_HOLDINGS[holding.symbol]) {
          // It's an ETF - add its underlying holdings
          ETF_HOLDINGS[holding.symbol].forEach(underlying => {
            const effectiveValue = holdingValue * (underlying.weight / 100);
            const current = exposureMap.get(underlying.symbol) || 0;
            exposureMap.set(underlying.symbol, current + effectiveValue);
          });
        } else {
          // It's a direct holding
          const current = exposureMap.get(holding.symbol) || 0;
          exposureMap.set(holding.symbol, current + holdingValue);
        }
      });

      return Array.from(exposureMap.entries())
        .map(([symbol, value]) => ({
          symbol,
          value,
          percentage: totalValue > 0 ? (value / totalValue) * 100 : 0,
          isETF: false,
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, topN);
    }
  };

  const positions = getPositions();

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-bold text-gray-900">{data.symbol}</p>
          <p className="text-sm text-gray-600 mb-2">{getCompanyName(data.symbol)}</p>
          <p className="text-sm text-gray-600">
            ${data.value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </p>
          <p className="text-sm font-medium text-primary-600">
            {data.percentage.toFixed(1)}% of portfolio
          </p>
          {data.isETF && !untangleETFs && (
            <p className="text-xs text-amber-600 mt-1">ETF (contains multiple stocks)</p>
          )}
        </div>
      );
    }
    return null;
  };

  const getBarColor = (symbol: string, _index: number, isETF: boolean) => {
    if (untangleETFs) {
      return getCompanyColor(symbol, _index);
    }
    return isETF ? '#f59e0b' : getCompanyColor(symbol, _index);
  };

  const topNOptions: TopN[] = [5, 10, 20];

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Top Positions</h3>
        
        <div className="flex items-center gap-4">
          {/* Top N Selector */}
          <div className="flex gap-2">
            {topNOptions.map((n) => (
              <button
                key={n}
                onClick={() => setTopN(n)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  topN === n
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Top {n}
              </button>
            ))}
          </div>

          {/* ETF Untangle Toggle */}
          <button
            onClick={() => setUntangleETFs(!untangleETFs)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              untangleETFs
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {untangleETFs ? '🔍 Untangled' : '📦 With ETFs'}
          </button>
        </div>
      </div>

      {untangleETFs && (
        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
          💡 <strong>Untangled view:</strong> ETFs are broken down into their underlying holdings to show your true exposure per company.
        </div>
      )}

      <ResponsiveContainer width="100%" height={Math.max(400, positions.length * 35)}>
        <BarChart data={positions} layout="vertical" margin={{ left: 60, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            type="number" 
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            tickFormatter={(value) => `${value.toFixed(1)}%`}
          />
          <YAxis 
            type="category" 
            dataKey="symbol" 
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            width={50}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="percentage" radius={[0, 8, 8, 0]}>
            {positions.map((entry, idx) => (
              <Cell key={`cell-${idx}`} fill={getBarColor(entry.symbol, idx, entry.isETF)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      {!untangleETFs && (
        <div className="flex items-center justify-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500" />
            <span className="text-gray-700">Individual Stocks</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-amber-500" />
            <span className="text-gray-700">ETFs</span>
          </div>
        </div>
      )}
    </div>
  );
}
